import { useState, useRef, useCallback } from 'react';
import { authAxios } from '../auth';
import { API } from '../config';

interface UseAudioRecorderResult {
    isRecording: boolean;
    isUploading: boolean;
    error: string | null;
    startRecording: () => Promise<void>;
    stopRecording: () => Promise<Blob | null>;
    uploadAudio: (language: string, word: string, blob: Blob) => Promise<boolean>;
    deleteAudio: (language: string, word: string) => Promise<boolean>;
    checkAudioExists: (language: string, word: string) => Promise<boolean>;
    getAudioUrl: (language: string, word: string) => string;
    clearError: () => void;
}

export const useAudioRecorder = (): UseAudioRecorderResult => {
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const startRecording = useCallback(async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Determine best supported format
            let mimeType = 'audio/webm';
            if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
                mimeType = 'audio/webm;codecs=opus';
            } else if (MediaRecorder.isTypeSupported('audio/webm')) {
                mimeType = 'audio/webm';
            } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
                mimeType = 'audio/mp4';
            }

            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;
            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorder.start();
            setIsRecording(true);
        } catch (err: any) {
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError('Microphone access denied. Please allow microphone access to record.');
            } else if (err.name === 'NotFoundError') {
                setError('No microphone found. Please connect a microphone.');
            } else {
                setError('Failed to start recording: ' + err.message);
            }
        }
    }, []);

    const stopRecording = useCallback(async (): Promise<Blob | null> => {
        return new Promise((resolve) => {
            if (!mediaRecorderRef.current) {
                resolve(null);
                return;
            }

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
                setIsRecording(false);
                resolve(blob);
            };

            // Stop all tracks to release the microphone
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
            mediaRecorderRef.current.stop();
        });
    }, []);

    const uploadAudio = useCallback(async (language: string, word: string, blob: Blob): Promise<boolean> => {
        setIsUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append('audio', blob, `${language}_${word}.webm`);
            await authAxios.post(API.audioUpload(language, word), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setIsUploading(false);
            return true;
        } catch (err: any) {
            setError('Failed to upload audio');
            setIsUploading(false);
            return false;
        }
    }, []);

    const checkAudioExists = useCallback(async (language: string, word: string): Promise<boolean> => {
        try {
            await authAxios.head(API.audioCheck(language, word));
            return true;
        } catch {
            return false;
        }
    }, []);

    const deleteAudio = useCallback(async (language: string, word: string): Promise<boolean> => {
        setError(null);
        try {
            await authAxios.delete(API.audioDelete(language, word));
            return true;
        } catch (err: any) {
            setError('Failed to delete audio');
            return false;
        }
    }, []);

    const getAudioUrl = useCallback((language: string, word: string): string => {
        return API.audioGet(language, word);
    }, []);

    return {
        isRecording,
        isUploading,
        error,
        startRecording,
        stopRecording,
        uploadAudio,
        deleteAudio,
        checkAudioExists,
        getAudioUrl,
        clearError,
    };
};
